import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { api, assetUrl } from "@/lib/api";
import { LocationLinks } from "@/components/PlaceCards";
import { useSeo } from "@/lib/seo";
import PublicLayout from "@/components/PublicLayout";
import { MapPin, Tag, ArrowLeft, Camera, SpinnerGap, ShieldCheck, CalendarBlank, ArrowRight, ShareNetwork, LinkSimple, Check, XLogo, FacebookLogo, WhatsappLogo, Fingerprint, Translate, Sparkle, Code, DownloadSimple } from "@phosphor-icons/react";

const REUSE_LABEL = {
  public_display: "Public display", public_attribution: "Reuse with attribution",
  commercial: "Commercial use OK", all_socialpulse: "Shared across SocialPulse",
};

const LANG_LABEL = { en: "English", hi: "हिन्दी", ta: "தமிழ்", ml: "മലയാളം", es: "Español", fr: "Français", de: "Deutsch" };

export default function PublicPhoto() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);
  const [lang, setLang] = useState("en");

  // Crawler-friendly share URL (serves Open Graph tags, then redirects into the app).
  const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/p/${id}`;
  const doCopy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  const nativeShare = async () => {
    if (navigator.share) { try { await navigator.share({ title: p?.title || "PhotoPulse", url: shareUrl }); } catch { /* cancelled */ } }
    else doCopy();
  };

  useEffect(() => {
    setP(null); setError(false); setLang("en");
    api.get(`/public/gallery/${id}`).then((r) => setP(r.data)).catch(() => setError(true));
  }, [id]);

  const plainDesc = p?.description ? p.description.replace(/<[^>]*>/g, " ").trim().slice(0, 160) : "";
  useSeo({
    title: p ? `${p.title || "Photo"} — PhotoPulse.pro` : "Photo — PhotoPulse.pro",
    description: plainDesc || "A public photo on PhotoPulse.pro",
    image: p ? assetUrl(p.image_url) : undefined,
  });

  if (error) return (
    <PublicLayout>
      <div className="mx-auto max-w-3xl px-5 py-24 text-center">
        <p className="font-display text-2xl font-black">Photo not available</p>
        <p className="mt-2 text-sm text-muted-foreground">It may be private or removed.</p>
        <Link to="/explore" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[#F27A54] px-5 py-2.5 text-sm font-bold text-white">Back to gallery</Link>
      </div>
    </PublicLayout>
  );

  if (!p) return <PublicLayout><div className="grid min-h-[60vh] place-items-center"><SpinnerGap size={36} className="animate-spin text-[#F27A54]" weight="bold" /></div></PublicLayout>;

  const tags = [...new Set([...(p.keywords || [])])].slice(0, 16);
  const dateStr = p.date_taken ? new Date(p.date_taken).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }) : null;

  const i18n = p.i18n || {};
  const langsAvailable = ["en", ...new Set([
    ...Object.keys(i18n.title || {}), ...Object.keys(i18n.description || {}), ...Object.keys(i18n.alt_text || {}),
  ])].filter((v) => v !== "en" ? true : true).filter((v, i, a) => a.indexOf(v) === i);
  const locTitle = (lang !== "en" && i18n.title?.[lang]) || p.title;
  const locDesc = (lang !== "en" && i18n.description?.[lang]) || p.description;

  const rights = p.rights || {};
  const creditName = p.credit_line || p.attribution?.text?.replace(/^Photo by /, "").replace(/ \/ PhotoPulse$/, "") || p.photographer || "PhotoPulse";
  const target = rights.attribution_target || "both";
  const attributionHtml = target === "photographer" && p.photographer_url
    ? `<a href="${p.photographer_url}" target="_blank" rel="noopener">${creditName}</a>`
    : target === "photo"
      ? `<a href="${p.canonical_url}" target="_blank" rel="noopener">${p.attribution?.text || `Photo by ${creditName} / PhotoPulse`}</a>`
      : `Photo by ${p.photographer_url ? `<a href="${p.photographer_url}" target="_blank" rel="noopener">${creditName}</a>` : creditName} on <a href="${p.canonical_url}" target="_blank" rel="noopener">PhotoPulse</a>`;
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.photo_id || p.id}/download`;
  const embedHtml = `<figure style="margin:0;max-width:640px">\n  <img src="${assetUrl(p.image_url)}" alt="${(p.alt_text || p.title || "").replace(/"/g, "'")}" style="width:100%;height:auto;border-radius:8px" />\n  <figcaption style="font:13px system-ui;color:#555;margin-top:6px">${attributionHtml}</figcaption>\n</figure>`;
  const copyText = async (text, setter) => {
    try { await navigator.clipboard.writeText(text); setter(true); setTimeout(() => setter(false), 2000); } catch { /* ignore */ }
  };

  return (
    <PublicLayout>
      <div className="mx-auto max-w-5xl px-5 py-8">
        <button onClick={() => nav(-1)} data-testid="photo-back" className="mb-5 flex items-center gap-1.5 text-sm font-semibold text-zinc-600 hover:text-[#F27A54]">
          <ArrowLeft size={16} weight="bold" /> Back
        </button>

        <div className="grid gap-8 lg:grid-cols-[1.5fr_1fr]">
          <div className="overflow-hidden rounded-xl bg-zinc-100 ring-1 ring-black/5">
            {p.media_type === "video" && p.video_url ? (
              <video data-testid="photo-video" src={assetUrl(p.video_url)} poster={assetUrl(p.image_url)}
                controls playsInline className="w-full object-contain bg-black" />
            ) : (
              <img data-testid="photo-image" src={assetUrl(p.image_url)} alt={p.alt_text || p.title || "photo"} className="w-full object-contain" />
            )}
          </div>

          <div className="min-w-0">
            {langsAvailable.length > 1 && (
              <div className="mb-3 flex items-center gap-1.5" data-testid="photo-lang-switch">
                <Translate size={14} weight="bold" className="text-[#2D6A4F]" />
                {langsAvailable.map((lg) => (
                  <button key={lg} onClick={() => setLang(lg)} data-testid={`photo-lang-${lg}`}
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${lang === lg ? "bg-[#2D6A4F] text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
                    {LANG_LABEL[lg] || lg.toUpperCase()}
                  </button>
                ))}
              </div>
            )}
            <h1 data-testid="photo-title" className="font-display text-2xl font-black leading-tight tracking-tight">{locTitle || "Untitled photograph"}</h1>

            {p.photo_id && (
              <p className="mt-1.5 flex items-center gap-1.5 text-[11px] font-medium text-zinc-400" data-testid="photo-ppid">
                <Fingerprint size={13} weight="bold" /> {p.photo_id}
              </p>
            )}

            {(p.location?.city || p.location?.country) && (
              <p className="mt-2 flex flex-wrap items-center gap-1.5 text-sm text-zinc-600" data-testid="photo-location">
                <MapPin size={15} weight="fill" className="text-[#F27A54]" />
                <LocationLinks location={p.location} className="text-zinc-600" />
                {p.location_basis === "gps" && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-[#2D6A4F]" data-testid="photo-loc-gps">from GPS</span>
                )}
                {p.location_basis === "ai" && (
                  <span className="flex items-center gap-0.5 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" data-testid="photo-loc-ai"><Sparkle size={10} weight="fill" /> AI-suggested</span>
                )}
              </p>
            )}
            {dateStr && <p className="mt-1 flex items-center gap-1.5 text-sm text-zinc-500"><CalendarBlank size={15} weight="bold" />{dateStr}</p>}

            {locDesc && (
              <div className="rich-html mt-4 text-sm leading-relaxed text-zinc-700"
                data-testid="photo-description" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(locDesc) }} />
            )}

            {(p.photographer || p.submitter?.name) && (
              <Link to={`/photographer/${encodeURIComponent(p.photographer || p.submitter?.name)}`}
                data-testid="photo-photographer-link"
                className="mt-5 flex items-center gap-3 rounded-lg border border-border bg-white p-3 transition-colors hover:border-[#F27A54]/50">
                {p.submitter?.avatar_url
                  ? <img src={p.submitter.avatar_url} alt="" className="h-9 w-9 rounded-full object-cover" />
                  : <span className="grid h-9 w-9 place-items-center rounded-full bg-[#F27A54]/10 text-[#F27A54]"><Camera size={16} weight="fill" /></span>}
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Photograph by</p>
                  <p className="truncate text-sm font-bold text-[#F27A54]">{p.photographer || p.submitter?.name}</p>
                </div>
              </Link>
            )}

            {REUSE_LABEL[p.reuse_permission] && (
              <p className="mt-4 flex items-center gap-1.5 rounded-md bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700" data-testid="photo-reuse">
                <ShieldCheck size={14} weight="fill" /> {REUSE_LABEL[p.reuse_permission]}
              </p>
            )}

            {/* Use this photo — attribution + embed (canonical link back to PhotoPulse) */}
            {["public_display", "public_attribution", "commercial", "all_socialpulse"].includes(p.reuse_permission) && (
              <div className="mt-5 rounded-xl border border-[#F27A54]/30 bg-[#FFF7F2] p-4" data-testid="use-this-photo">
                <p className="flex items-center gap-1.5 text-sm font-bold text-[#B4451F]"><Code size={16} weight="bold" /> Use this photo</p>
                <p className="mt-1 text-xs text-zinc-600">Free to reuse with attribution. Every use links back to this canonical PhotoPulse page.</p>

                <div className="mt-2 flex flex-wrap gap-1.5" data-testid="license-badges">
                  {rights.commercial_ok
                    ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Commercial OK</span>
                    : <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-[10px] font-semibold text-zinc-600">Non-commercial</span>}
                  {rights.editorial_only && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Editorial only</span>}
                  {rights.require_attribution && <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Attribution required</span>}
                </div>

                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={downloadUrl} data-testid="download-photo"
                    className="inline-flex items-center gap-1.5 rounded-md bg-[#F27A54] px-3 py-2 text-xs font-semibold text-white hover:bg-[#d9633f]">
                    <DownloadSimple size={14} weight="bold" /> {rights.allow_download ? "Download" : "Download preview"}
                  </a>
                  {!rights.allow_download && <span className="self-center text-[10px] text-zinc-500">web-size · watermarked</span>}
                </div>

                <div className="mt-3 space-y-2">
                  <div>
                    <p className="mb-1 overline text-[10px] text-zinc-500">Attribution (HTML)</p>
                    <div className="flex min-w-0 items-start gap-2">
                      <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap break-all rounded-md bg-white px-2.5 py-2 text-[11px] text-zinc-700 ring-1 ring-border">{attributionHtml}</code>
                      <button onClick={() => copyText(attributionHtml, setCopied)} data-testid="copy-attribution"
                        className="shrink-0 rounded-md bg-[#2D6A4F] px-2.5 py-2 text-[11px] font-semibold text-white hover:bg-[#245840]">{copied ? <Check size={13} weight="bold" /> : "Copy"}</button>
                    </div>
                  </div>
                  {rights.allow_embed && (
                    <div>
                      <p className="mb-1 overline text-[10px] text-zinc-500">Embed (HTML)</p>
                      <div className="flex min-w-0 items-start gap-2">
                        <code className="min-w-0 flex-1 overflow-auto whitespace-pre-wrap break-all rounded-md bg-white px-2.5 py-2 text-[11px] leading-snug text-zinc-700 ring-1 ring-border" style={{ maxHeight: 120 }}>{embedHtml}</code>
                        <button onClick={() => copyText(embedHtml, setEmbedCopied)} data-testid="copy-embed"
                          className="shrink-0 rounded-md bg-[#2D6A4F] px-2.5 py-2 text-[11px] font-semibold text-white hover:bg-[#245840]">{embedCopied ? <Check size={13} weight="bold" /> : "Copy"}</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-5" data-testid="photo-share">
              <p className="mb-1.5 flex items-center gap-1 overline text-[10px] text-muted-foreground"><ShareNetwork size={12} weight="fill" /> Share this photo</p>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={nativeShare} data-testid="photo-share-btn"
                  className="flex items-center gap-1.5 rounded-full bg-[#F27A54] px-3.5 py-2 text-xs font-bold text-white hover:bg-[#d9633f]">
                  <ShareNetwork size={14} weight="fill" /> Share
                </button>
                <button onClick={doCopy} data-testid="photo-copy-link"
                  className="flex items-center gap-1.5 rounded-full border border-border px-3.5 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-100">
                  {copied ? <><Check size={14} weight="bold" className="text-emerald-600" /> Copied!</> : <><LinkSimple size={14} weight="bold" /> Copy link</>}
                </button>
                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(p.title || "PhotoPulse")}`}
                  target="_blank" rel="noopener noreferrer" aria-label="Share on X"
                  className="grid h-9 w-9 place-items-center rounded-full border border-border text-zinc-700 hover:bg-zinc-100"><XLogo size={15} weight="fill" /></a>
                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                  target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook"
                  className="grid h-9 w-9 place-items-center rounded-full border border-border text-zinc-700 hover:bg-zinc-100"><FacebookLogo size={15} weight="fill" /></a>
                <a href={`https://wa.me/?text=${encodeURIComponent((p.title || "PhotoPulse") + " " + shareUrl)}`}
                  target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp"
                  className="grid h-9 w-9 place-items-center rounded-full border border-border text-zinc-700 hover:bg-zinc-100"><WhatsappLogo size={15} weight="fill" /></a>
              </div>
            </div>

            {tags.length > 0 && (
              <div className="mt-4" data-testid="photo-tags">
                <p className="mb-1.5 flex items-center gap-1 overline text-[10px] text-muted-foreground"><Tag size={12} weight="fill" /> Tags</p>
                <div className="flex flex-wrap gap-1.5">
                  {tags.map((t) => (
                    <Link key={t} to={`/explore?tag=${encodeURIComponent(t)}`}
                      className="rounded-sm bg-blue-50 px-2 py-0.5 text-xs font-medium text-[#F27A54] hover:bg-blue-100">{t}</Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 rounded-lg bg-[#F27A54] p-4 text-white">
              <p className="font-display text-sm font-bold">Have photos like this?</p>
              <p className="mt-1 text-xs text-white/80">Add yours to the library and share them across the Pulse network.</p>
              <Link to="/login" data-testid="photo-contribute" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-sm font-bold text-[#F27A54] hover:bg-white/90">
                Contribute <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
